import { useEffect, useRef, useState } from 'react';
import { getLeaderboard, type LeaderboardResponse } from '../../api/leaderboard';

interface Props {
  /** When provided, leaderboard scopes to reps assigned to this campaign.
   * Caller is always included so they see their own rank even if not
   * assigned. */
  campaignId?: string | null;
  onClose: () => void;
}

// Leaderboard pop-in. Mounted only when the runner's mid-call guard allows
// it (see SprintRunner.tsx). Bottom-right toast — non-modal, non-blocking
// — so the rep can dismiss with a glance and keep dialling.
//
// Refresh strategy: fetch once on mount. We initially shipped a Realtime
// subscription (R5/C2) but security review found it required a too-broad
// SELECT policy on points_ledger. Reverted to single-fetch; for true live
// updates, a future PR will emit aggregate "leaderboard pings" from
// record_disposition into a public-safe table.
//
// Auto-dismisses after 8s. The timer is anchored to mount (not to onClose
// identity) so it isn't reset every parent re-render — the parent's
// queue-poll setInterval would otherwise cause the timer to never fire.

const AUTO_DISMISS_MS = 8000;

export function LeaderboardPopover({ campaignId, onClose }: Props) {
  const [data, setData] = useState<LeaderboardResponse | null>(null);

  // Latest onClose in a ref so the auto-dismiss timer effect can run once
  // per mount instead of resetting on every parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Initial fetch.
  useEffect(() => {
    let cancelled = false;
    void getLeaderboard(campaignId ?? null)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch(() => {
        if (cancelled) return;
        onCloseRef.current();
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  // Auto-dismiss after 8s. Empty deps so the timer doesn't reset on each
  // parent render — onClose is read via ref at fire time.
  useEffect(() => {
    const t = setTimeout(() => onCloseRef.current(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, []);

  if (!data) return null;

  return (
    <aside
      role="status"
      aria-live="polite"
      className="pointer-events-auto fixed bottom-4 right-4 z-30 w-80 rounded-lg border border-graphite-100 bg-surface p-4 shadow-lg"
    >
      <header className="flex items-baseline justify-between">
        <p className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
          Leaderboard · today
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss leaderboard"
          className="font-mono text-xs text-graphite-500 hover:text-graphite-900"
        >
          ×
        </button>
      </header>
      <ol className="mt-3 space-y-1.5">
        {data.top.map((row) => (
          <li
            key={row.user_id}
            className="flex items-baseline justify-between font-sans text-sm text-graphite-900"
          >
            <span>
              <span className="font-mono text-xs text-graphite-500">{row.rank}.</span> {row.label}
            </span>
            <span className="font-mono text-xs tabular-nums text-graphite-700">
              {row.points_today} pts
            </span>
          </li>
        ))}
      </ol>
      <div className="mt-3 border-t border-graphite-100 pt-3">
        <p className="flex items-baseline justify-between font-sans text-sm">
          <span className="text-graphite-700">
            <span className="font-mono text-xs text-graphite-500">{data.me.rank}.</span> You
          </span>
          <span className="font-mono text-xs tabular-nums text-graphite-700">
            {data.me.points_today} pts
          </span>
        </p>
        {data.points_to_next_rank !== null ? (
          <p className="mt-1 font-mono text-[0.7rem] text-accent-progress">
            {data.points_to_next_rank} {data.points_to_next_rank === 1 ? 'pt' : 'pts'} to rank{' '}
            {data.me.rank - 1}.
          </p>
        ) : data.me.rank === 1 ? (
          <p className="mt-1 font-mono text-[0.7rem] text-accent-progress">Top of the table.</p>
        ) : null}
      </div>
    </aside>
  );
}
